import { Component, Inject, signal, computed, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroXMark,
  heroUsers,
  heroCheckCircle,
  heroXCircle,
  heroClock,
  heroBuildingOffice,
  heroMagnifyingGlass,
  heroFunnel
} from '@ng-icons/heroicons/outline';
import { UserAvatarComponent } from '../../../shared/user-avatar/user-avatar.component';
import { TimeAgoPipe } from '../../../core/pipes/timeAgo.pipe';
import { environment } from '../../../../environments/environment';
import { UserRequest } from '../../../models/user-request.model';

@Component({
  selector: 'app-user-requests-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatTabsModule,
    MatTooltipModule,
    FormsModule,
    NgIcon,
    UserAvatarComponent,
    TimeAgoPipe
  ],
  providers: [
    provideIcons({
      heroXMark,
      heroUsers,
      heroCheckCircle,
      heroXCircle,
      heroClock,
      heroBuildingOffice,
      heroMagnifyingGlass,
      heroFunnel
    })
  ],
  templateUrl: './user-requests-modal.component.html',
  styleUrl: './user-requests-modal.component.scss'
})
export class UserRequestsModalComponent implements OnInit {
  profileApiUrl = environment.profileApiUrl;
  acceptingRequest = false;
  rejectingRequest = false;
  searchQuery = '';
  activeFilter = signal<string>('all');

  filterOptions = [
    { label: 'All', value: 'all' },
    { label: 'Pending', value: 'pending' },
    { label: 'Accepted', value: 'accepted' },
    { label: 'Declined', value: 'rejected' }
  ];

  filteredRequests = computed(() => {
    let filtered = this.data.requests;

    // Filter by status
    if (this.activeFilter() !== 'all') {
      filtered = filtered.filter(request => request.status === this.activeFilter());
    }

    // Filter by search query
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(request =>
        request.fromUser.firstName.toLowerCase().includes(query) ||
        request.fromUser.lastName.toLowerCase().includes(query) ||
        request.fromUser.company.toLowerCase().includes(query) ||
        request.fromUser.position.toLowerCase().includes(query)
      );
    }

    return filtered;
  });

  constructor(
    public dialogRef: MatDialogRef<UserRequestsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      requests: UserRequest[];
      initialFilter?: string; // Add optional initialFilter
    }
  ) { }

  ngOnInit() {
    // Set initial filter if provided in data
    if (this.data.initialFilter) {
      this.activeFilter.set(this.data.initialFilter);
    }
  }

  onClose() {
    this.dialogRef.close();
  }

  onAcceptRequest(request: UserRequest) {
    this.acceptingRequest = true;

    setTimeout(() => {
      this.dialogRef.close({ action: 'accept', request });
    }, 500);
  }

  onRejectRequest(request: UserRequest) {
    this.rejectingRequest = true;

    setTimeout(() => {
      this.dialogRef.close({ action: 'reject', request });
    }, 500); 
  }

  setActiveFilter(filter: string) {
    this.activeFilter.set(filter);
  }

  updateSearch() {
    // Trigger computed update
  }

  getFilterButtonClass(filter: string): string {
    return this.activeFilter() === filter
      ? 'tw-bg-indigo-500 tw-text-white tw-shadow-sm'
      : 'tw-text-slate-600 dark:tw-text-slate-400 hover:tw-text-slate-900 dark:hover:tw-text-slate-200 hover:tw-bg-slate-100 dark:hover:tw-bg-slate-700';
  }

  getFilterBadgeClass(filter: string): string {
    return this.activeFilter() === filter
      ? 'tw-bg-white/20 tw-text-white'
      : 'tw-bg-slate-200 dark:tw-bg-slate-600 tw-text-slate-600 dark:tw-text-slate-300';
  }

  getFilterCount(filter: string): number {
    if (filter === 'all') {
      return this.data.requests.length;
    }
    return this.data.requests.filter(request => request.status === filter).length;
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'pending': return 'heroClock';
      case 'accepted': return 'heroCheckCircle';
      case 'rejected': return 'heroXCircle';
      default: return 'heroClock';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'pending': return 'Pending';
      case 'accepted': return 'Connected';
      case 'rejected': return 'Declined';
      default: return 'Unknown';
    }
  }
}
